import { ovl } from "./"
import { Data } from "./dataapi"
import { _RenderTracking } from "./tracker/tracker"

type LogLevel = "INFO" | "WARN" | "ERROR"

interface LogEntry {
  Level: LogLevel
  Message: string
  StackTrace?: string
  Timestamp: string
  Url: string
}

export class ClientLogger {
  private buffer: LogEntry[] = []
  private readonly MAX_BUFFER_SIZE = 50
  private flushTimer: any = null
  private originalConsole: any = {}
  private isProcessing: boolean = false

  private getDebounceTime(): number {
    return ovl.getState().ovl.app.consoleToAudit?.ClientSendDebounceMS || 60000
  }

  constructor() {
    this.init()
  }

  private init() {
    // Save original console methods
    this.originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
    }

    // Override console methods
    console.log = (...args) => {
      this.originalConsole.log.apply(console, args)
      this.addLog("INFO", args)
    }

    console.warn = (...args) => {
      this.originalConsole.warn.apply(console, args)
      this.addLog("WARN", args)
    }

    console.error = (...args) => {
      this.originalConsole.error.apply(console, args)
      this.addLog("ERROR", args)
    }

    // Global error handlers
    window.onerror = (msg, url, lineNo, columnNo, error) => {
      this.addLog("ERROR", [msg, `Line: ${lineNo}, Col: ${columnNo}`, error])
      return false
    }

    window.onunhandledrejection = (event) => {
      this.addLog("ERROR", ["Unhandled Rejection:", event.reason])
    }

    // Flush on unload
    window.addEventListener("beforeunload", () => {
      this.flush(true)
    })
  }

  private addLog(level: LogLevel, args: any[]) {
    // Check if logging is enabled
    _RenderTracking.pauseTracking = true
    try {
      const config = ovl.getState().ovl.app.consoleToAudit
      if (config) {
        if (!config.Enabled) {
          return
        }
        const minLevel = this.getLogLevelValue(config.LogLevel)
        const currentLevel = this.getLogLevelValue(level)
        if (currentLevel < minLevel) {
          return
        }
      }

      if (this.isProcessing) return // Prevent recursive logging loop
      this.isProcessing = true

      try {
        const message = args
          .map((a) => {
            try {
              return typeof a === "object" ? JSON.stringify(a) : String(a)
            } catch (e) {
              return String(a)
            }
          })
          .join(" ")

        let stackTrace = ""
        const errorArg = args.find((a) => a instanceof Error)
        if (errorArg) {
          stackTrace = errorArg.stack || ""
        }

        const entry: LogEntry = {
          Level: level,
          Message: message,
          StackTrace: stackTrace,
          Timestamp: new Date().toISOString(),
          Url: window.location.href,
        }

        this.buffer.push(entry)

        if (this.buffer.length >= this.MAX_BUFFER_SIZE) {
          this.flush()
        } else {
          if (this.flushTimer) {
            clearTimeout(this.flushTimer)
          }
          this.flushTimer = setTimeout(
            () => this.flush(),
            this.getDebounceTime()
          )
        }
      } catch (e) {
        this.originalConsole.error("Failed to capture log", e)
      } finally {
        this.isProcessing = false
      }
    } finally {
      _RenderTracking.pauseTracking = false
    }
  }

  private getLogLevelValue(level: string): number {
    switch (level?.toUpperCase()) {
      case "ERROR":
        return 3
      case "WARN":
        return 2
      case "INFO":
        return 1
      default:
        return 0
    }
  }

  private async flush(sync = false) {
    if (this.buffer.length === 0) return

    const dataToSend = [...this.buffer]
    this.buffer = []

    if (this.flushTimer) {
      clearTimeout(this.flushTimer)
      this.flushTimer = null
    }

    try {
      // Use suppressFeedbackSnack to avoid UI popup on log failure
      await Data("ClientLog", dataToSend, { suppressFeedbackSnack: true })
    } catch (err) {
      // Failed to send, maybe print to original console but don't re-log
      this.originalConsole.error("Failed to send client logs", err)
    }
  }
}
